import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  Shield, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  FileText,
  User,
  Calendar,
  Hash,
  Search,
  Loader2
} from 'lucide-react';
import api from '../../lib/api';

interface VerificationResult {
  valid: boolean;
  contract?: {
    verificationCode: string;
    status: string;
    templateName: string;
    templateCategory: string;
    createdAt: string;
    completedAt?: string;
    documentHash: string;
    signers: {
      role: string;
      name: string;
      status: string;
      signedAt?: string;
    }[];
  };
  error?: string;
}

const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Rascunho',
  PENDING_SIGNATURES: 'Aguardando Assinaturas',
  PARTIALLY_SIGNED: 'Parcialmente Assinado',
  COMPLETED: 'Concluído',
  CANCELLED: 'Cancelado',
  EXPIRED: 'Expirado',
};

export function ContractVerification() {
  const { code } = useParams<{ code: string }>();
  const [searchParams] = useSearchParams();
  const [manualCode, setManualCode] = useState(code || searchParams.get('code') || '');
  const [searchCode, setSearchCode] = useState(code || searchParams.get('code') || '');

  const { data: result, isLoading, error } = useQuery({
    queryKey: ['verify-contract', searchCode],
    queryFn: async () => {
      const response = await api.get<VerificationResult>(`/verify/${searchCode}`);
      return response.data;
    },
    enabled: !!searchCode,
    retry: false,
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchCode(manualCode);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-3xl mx-auto px-6 py-8 text-center">
          <Shield className="w-16 h-16 text-blue-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            Verificação de Documento
          </h1>
          <p className="text-gray-600">
            Verifique a autenticidade de um documento assinado digitalmente
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8">
        {/* Search form */}
        <form onSubmit={handleSearch} className="mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Código de Verificação
            </label>
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={manualCode}
                  onChange={(e) => setManualCode(e.target.value)}
                  placeholder="Digite o código de verificação"
                  className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <button
                type="submit"
                disabled={!manualCode || isLoading}
                className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Search className="w-5 h-5" />
                )}
                Verificar
              </button>
            </div>
          </div>
        </form>

        {/* Loading */}
        {isLoading && (
          <div className="text-center py-12">
            <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Verificando documento...</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-red-800 mb-2">
              Documento Não Encontrado
            </h2>
            <p className="text-red-600">
              Não foi possível encontrar um documento com este código de verificação.
              Verifique se o código está correto e tente novamente.
            </p>
          </div>
        )}

        {/* Result */}
        {result && !isLoading && (
          <div className="space-y-6">
            {/* Validity status */}
            <div className={`rounded-lg p-6 ${
              result.valid 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-red-50 border border-red-200'
            }`}>
              <div className="flex items-center gap-4">
                {result.valid ? (
                  <CheckCircle className="w-12 h-12 text-green-600" />
                ) : (
                  <XCircle className="w-12 h-12 text-red-600" />
                )}
                <div>
                  <h2 className={`text-xl font-semibold ${
                    result.valid ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {result.valid ? 'Documento Válido' : 'Documento Inválido'}
                  </h2>
                  <p className={result.valid ? 'text-green-600' : 'text-red-600'}>
                    {result.valid 
                      ? 'Este documento foi assinado digitalmente e é autêntico.'
                      : result.error || 'Este documento não pôde ser verificado.'
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Contract details */}
            {result.contract && (
              <>
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-gray-500" />
                    Informações do Documento
                  </h3>
                  <dl className="grid grid-cols-2 gap-4">
                    <div>
                      <dt className="text-sm text-gray-500">Tipo de Documento</dt>
                      <dd className="font-medium">{result.contract.templateName}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500">Categoria</dt>
                      <dd className="font-medium">{result.contract.templateCategory}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500">Status</dt>
                      <dd>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm ${
                          result.contract.status === 'COMPLETED'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {result.contract.status === 'COMPLETED' ? (
                            <CheckCircle className="w-4 h-4" />
                          ) : (
                            <AlertTriangle className="w-4 h-4" />
                          )}
                          {STATUS_LABELS[result.contract.status] || result.contract.status}
                        </span>
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500">Código de Verificação</dt>
                      <dd className="font-mono">{result.contract.verificationCode}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500">Data de Criação</dt>
                      <dd className="flex items-center gap-1">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        {format(new Date(result.contract.createdAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                      </dd>
                    </div>
                    {result.contract.completedAt && (
                      <div>
                        <dt className="text-sm text-gray-500">Data de Conclusão</dt>
                        <dd className="flex items-center gap-1">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          {format(new Date(result.contract.completedAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                        </dd>
                      </div>
                    )}
                  </dl>

                  {/* Document hash */}
                  <div className="mt-4 pt-4 border-t">
                    <dt className="text-sm text-gray-500 mb-1">Hash do Documento (SHA-256)</dt>
                    <dd className="font-mono text-xs bg-gray-50 p-2 rounded break-all">
                      {result.contract.documentHash}
                    </dd>
                  </div>
                </div>

                {/* Signers */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <User className="w-5 h-5 text-gray-500" />
                    Signatários
                  </h3>
                  <div className="space-y-3">
                    {result.contract.signers.map((signer, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            signer.status === 'SIGNED' 
                              ? 'bg-green-100' 
                              : 'bg-gray-200'
                          }`}>
                            {signer.status === 'SIGNED' ? (
                              <CheckCircle className="w-5 h-5 text-green-600" />
                            ) : (
                              <User className="w-5 h-5 text-gray-400" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium">{signer.name}</p>
                            <p className="text-sm text-gray-500">{signer.role}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          {signer.status === 'SIGNED' && signer.signedAt ? (
                            <p className="text-sm text-green-600">
                              Assinado em {format(new Date(signer.signedAt), "dd/MM/yyyy", { locale: ptBR })}
                            </p>
                          ) : (
                            <p className="text-sm text-gray-400">Pendente</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Disclaimer */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Nota:</strong> Esta verificação confirma que o documento foi assinado 
                digitalmente através da plataforma UniãoBR. Para informações detalhadas sobre 
                o conteúdo do documento, entre em contato com as partes envolvidas.
              </p>
            </div>
          </div>
        )}

        {/* Initial state */}
        {!searchCode && !isLoading && (
          <div className="text-center py-12 text-gray-500">
            <Shield className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p>Digite um código de verificação para validar um documento</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t bg-white mt-auto">
        <div className="max-w-3xl mx-auto px-6 py-4 text-center text-sm text-gray-500">
          <p>
            Verificação de documentos digitais • UniãoBR Platform
          </p>
        </div>
      </div>
    </div>
  );
}
